import { apiClient } from './apiClient';
const API_PATH = '/settings';

class SettingsService {
  constructor() {
    // Standard-Settings als Fallback
    this.defaultSettings = {
      minutes_per_athlete: {
        value: '2',
        description: 'Minuten pro Athlet für die Zeitplanberechnung'
      },
      buffer_minutes: {
        value: '3',
        description: 'Pufferzeit in Minuten zwischen Gruppen'
      },
      daily_start_time: {
        value: '09:00:00',
        description: 'Startzeit pro Tag (HH:MM:SS)'
      },
      daily_end_time: {
        value: '17:00:00',
        description: 'Endzeit pro Tag, ab wann zum nächsten Tag gewechselt wird (HH:MM:SS)'
      },
      competition_start_date: {
        value: '2026-07-15',
        description: 'Startdatum des Wettkampfs (YYYY-MM-DD)'
      },
      competition_end_date: {
        value: '2026-07-17',
        description: 'Enddatum des Wettkampfs (YYYY-MM-DD)'
      }
    };
  }

  // Simuliert API-Verzögerung
  async delay(ms = 200) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Alle Settings abrufen
  async getAllSettings() {
    try {
      const data = await apiClient.get(API_PATH);
      return { success: true, data: data || {}, total: Object.keys(data || {}).length };
    } catch (e) {
      console.warn('API-Fehler, verwende Standard-Einstellungen:', e.message);
      return { 
        success: true, 
        data: this.defaultSettings, 
        total: Object.keys(this.defaultSettings).length, 
        fallback: true 
      };
    }
  }

  // Einzelne Setting abrufen
  async getSettingByKey(key) {
    try {
      const data = await apiClient.get(`${API_PATH}/${key}`);
      return { success: true, data };
    } catch (e) {
      const setting = this.defaultSettings[key];
      if (!setting) return { success: false, error: 'Einstellung nicht gefunden', data: null, fallback: true };
      return { success: true, data: { key, ...setting }, fallback: true };
    }
  }

  // Setting aktualisieren
  async updateSetting(key, value, description) {
    try {
      const data = await apiClient.put(`${API_PATH}/${key}`, { value, description });
      return { success: true, data, message: 'Einstellung erfolgreich aktualisiert' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Mehrere Settings auf einmal aktualisieren
  async updateMultipleSettings(settingsArray) {
    try {
      const data = await apiClient.put(API_PATH, { settings: settingsArray });
      return { success: true, data, message: 'Einstellungen erfolgreich aktualisiert' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Settings auf Standardwerte zurücksetzen
  async resetSettings() {
    try {
      const data = await apiClient.post(`${API_PATH}/reset`);
      return { success: true, data, message: 'Einstellungen erfolgreich zurückgesetzt' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Alle dynamischen Daten zurücksetzen (Teilnehmer, Gruppen, Ergebnisse, Zeitplan)
  async resetAllData(deleteOptions) {
    try {
      const data = await apiClient.post(`${API_PATH}/reset-data`, { delete: deleteOptions });
      return { success: true, data, message: 'Daten erfolgreich zurückgesetzt' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Demo-Daten generieren
  async seedDemoData(options) {
    try {
      const data = await apiClient.post(`${API_PATH}/seed-demo`, options || {});
      return { success: true, data, message: 'Demo-Daten erfolgreich generiert' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Hilfsfunktion: Settings in Array-Format konvertieren
  settingsToArray(settingsObject) {
    return Object.entries(settingsObject).map(([key, setting]) => ({
      key,
      value: setting.value,
      description: setting.description
    }));
  }

  // Hilfsfunktion: Array in Settings-Objekt konvertieren
  arrayToSettings(settingsArray) {
    const settingsObject = {};
    settingsArray.forEach(setting => {
      settingsObject[setting.key] = {
        value: setting.value,
        description: setting.description
      };
    });
    return settingsObject;
  }
}

// Singleton-Instanz exportieren
const settingsService = new SettingsService();
export default settingsService; 